import { Trans } from "@lingui/macro";
import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { ethers } from "ethers";

import { getServerUrl } from "config/backend";
import { ARBITRUM, BSC } from "config/chains";
import { USD_DECIMALS } from "config/factors";
import { useMoneyPrice, useTotalMoneyStaked, usePositionStates, useVolumeData } from "domain/legacy";
import { useV1FeesInfo, useVolumeInfo } from "domain/stats";
import useV2Stats from "domain/synthetics/stats/useV2Stats";
import { bigMath } from "lib/bigmath";
import { useChainId } from "lib/chains";
import { arrayURLFetcher } from "lib/fetcher";
import { MLP_DECIMALS, MONEY_DECIMALS } from "lib/legacy";
import { expandDecimals, formatAmount, formatAmountHuman } from "lib/numbers";
import { sumBigInts } from "lib/sumBigInts";
import useWallet from "lib/wallets/useWallet";
import { ACTIVE_CHAIN_IDS } from "./DashboardV2";
import { getFormattedFeesDuration } from "./getFormattedFeesDuration";
import { getPositionStats } from "./getPositionStats";
import type { ChainStats } from "./useDashboardChainStatsMulticall";
import { useInfoTokens } from "domain/tokens";
import ChainsStatsTooltipRow from "components/StatsTooltip/ChainsStatsTooltipRow";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import TooltipComponent from "components/Tooltip/Tooltip";

export function OverviewCard({
  statsArbitrum,
  statsBsc,
}: {
  statsArbitrum?: ChainStats;
  statsBsc?: ChainStats;
}) {
  const { active, signer } = useWallet();
  const { chainId } = useChainId();

  const v2ArbitrumOverview = useV2Stats(ARBITRUM);
  const v2BscOverview = useV2Stats(BSC);

  // const { data: positionStats } = useSWR<
  //   {
  //     totalActivePositions: number;
  //     totalLongPositionSizes: string;
  //     totalLongPositionCollaterals: string;
  //     totalShortPositionCollaterals: string;
  //     totalShortPositionSizes: string;
  //     openInterest: bigint;
  //   }[]
  // >(
  //   ACTIVE_CHAIN_IDS.map((chainId) => getServerUrl(chainId, "/position_stats")),
  //   {
  //     fetcher: arrayURLFetcher,
  //   }
  // );

const { infoTokens } = useInfoTokens(signer, chainId, active);

// normalize from 1e18 → 1e30 for correct USD precision
const totalLongOiRaw = Object.values(infoTokens)
  .filter((t) => !t.isStable && !t.isNative)
  .reduce((sum, t) => sum + (t.guaranteedUsd ?? 0n), 0n);

const totalShortOiRaw = Object.values(infoTokens)
  .filter((t) => !t.isStable && !t.isNative)
  .reduce((sum, t) => sum + (t.globalShortSize ?? 0n), 0n);

const positionStatsInfo = {
  totalLongPositionSizes: totalLongOiRaw,
  totalShortPositionSizes: totalShortOiRaw,
  openInterest: totalLongOiRaw + totalShortOiRaw,
};

  const v1ArbitrumFees = useV1FeesInfo(ARBITRUM);
  const v1BscFees = useV1FeesInfo(BSC);

  const { moneyPrice } = useMoneyPrice(chainId, { arbitrum: chainId === ARBITRUM ? signer : undefined }, active);

  let { [BSC]: stakedMoneyBsc, [ARBITRUM]: stakedMoneyArbitrum } = useTotalMoneyStaked();

  // #region TVL and MLP Pool
  const mlpTvlArbitrum = statsArbitrum?.mlp.aum;
  const mlpTvlBsc = statsBsc?.mlp.aum;

  const mlpSupplyArbitrum = statsArbitrum?.reader?.tokenBalancesWithSupplies?.mlpSupply;
  const mlpSupplyBsc = statsBsc?.reader?.tokenBalancesWithSupplies?.mlpSupply;

  const mlpPriceArbitrum =
    mlpTvlArbitrum !== undefined && mlpTvlArbitrum > 0n && mlpSupplyArbitrum !== undefined
      ? bigMath.mulDiv(mlpTvlArbitrum, expandDecimals(1, MLP_DECIMALS), mlpSupplyArbitrum)
      : expandDecimals(1, USD_DECIMALS);

  const mlpPriceBsc =
    mlpTvlBsc !== undefined && mlpTvlBsc > 0n && mlpSupplyBsc !== undefined
      ? bigMath.mulDiv(mlpTvlBsc, expandDecimals(1, MLP_DECIMALS), mlpSupplyBsc)
      : expandDecimals(1, USD_DECIMALS);

  const mlpMarketCapArbitrum =
    mlpSupplyArbitrum !== undefined
      ? bigMath.mulDiv(mlpPriceArbitrum, mlpSupplyArbitrum, expandDecimals(1, MLP_DECIMALS))
      : undefined;

  const mlpMarketCapBsc =
    mlpSupplyBsc !== undefined
      ? bigMath.mulDiv(mlpPriceBsc, mlpSupplyBsc, expandDecimals(1, MLP_DECIMALS))
      : undefined;

  // const totalMlpTvl =
  //   mlpTvlArbitrum !== undefined && mlpTvlBsc !== undefined ? mlpTvlArbitrum + mlpTvlBsc : undefined;
  const totalMlpTvl =
    mlpTvlBsc !== undefined ? mlpTvlBsc : undefined;

  const gmTvlArbitrum = v2ArbitrumOverview.totalGMLiquidity;
  const gmTvlBsc = v2BscOverview.totalGMLiquidity;

  const totalGmTvl = gmTvlArbitrum + gmTvlBsc;

  let displayTvlArbitrum: bigint | undefined = undefined;
  let displayTvlBsc: bigint | undefined = undefined;
  let displayTvl: bigint | undefined = undefined;
  if (
    moneyPrice !== undefined &&
    stakedMoneyArbitrum !== undefined &&
    stakedMoneyBsc !== undefined &&
    mlpMarketCapArbitrum !== undefined &&
    mlpMarketCapBsc !== undefined
  ) {
    const stakedMoneyUsdArbitrum = bigMath.mulDiv(moneyPrice, stakedMoneyArbitrum, expandDecimals(1, MONEY_DECIMALS));
    const stakedMoneyUsdBsc = bigMath.mulDiv(moneyPrice, stakedMoneyBsc, expandDecimals(1, MONEY_DECIMALS));

    // MONEY Staked + MLP Pools + GM Pools
    displayTvlArbitrum = stakedMoneyUsdArbitrum + mlpMarketCapArbitrum + gmTvlArbitrum;
    displayTvlBsc = stakedMoneyUsdBsc + mlpMarketCapBsc + gmTvlBsc;
    // displayTvl = displayTvlArbitrum + displayTvlBsc;
    displayTvl = displayTvlBsc;
  }

  // #endregion TVL and MLP Pool

  // #region Daily Volume
  const v1DailyVolumeInfo = useVolumeInfo();
  const [totalVolume, totalVolumeDelta] = useVolumeData();

  const v1ArbitrumDailyVolume = v1DailyVolumeInfo?.[ARBITRUM];
  // const v1BscDailyVolume = v1DailyVolumeInfo?.[BSC];
  const v1BscDailyVolume = totalVolumeDelta ? totalVolumeDelta : 0;

  const v2ArbitrumDailyVolume = v2ArbitrumOverview.dailyVolume;
  const v2BscDailyVolume = v2BscOverview.dailyVolume;

  // const totalDailyVolume = sumBigInts(
  //   // v1ArbitrumDailyVolume,
  //   v1BscDailyVolume,
  //   // v2ArbitrumDailyVolume,
  //   // v2BscDailyVolume
  // );
  const totalDailyVolume = v1BscDailyVolume;
  // #endregion Daily Volume

  // #region Open Interest
  const v1ArbitrumOpenInterest = positionStatsInfo?.[ARBITRUM]?.openInterest;
  const v1BscOpenInterest = positionStatsInfo?.[BSC]?.openInterest;
  const v2ArbitrumOpenInterest = v2ArbitrumOverview.openInterest;
  const v2BscOpenInterest = v2BscOverview.openInterest;

  const totalOpenInterest = sumBigInts(
    v1ArbitrumOpenInterest,
    v1BscOpenInterest,
    v2ArbitrumOpenInterest,
    v2BscOpenInterest
  );
  // #endregion Open Interest

  // #region Long Position Sizes
  const v1ArbitrumLongPositionSizes = positionStatsInfo?.[ARBITRUM]?.totalLongPositionSizes;
  const v1BscLongPositionSizes = positionStatsInfo?.totalLongPositionSizes
    ? positionStatsInfo.totalLongPositionSizes
    : 0n;


  const v2ArbitrumLongPositionSizes = v2ArbitrumOverview.totalLongPositionSizes;
  const v2BscLongPositionSizes = v2BscOverview.totalLongPositionSizes;

  const totalLongPositionSizes = sumBigInts(
    // v1ArbitrumLongPositionSizes,
    v1BscLongPositionSizes,
    // Array.isArray(positionStatsInfo) ? undefined : positionStatsInfo?.totalLongPositionSizes,
    // v2ArbitrumLongPositionSizes,
    // v2BscLongPositionSizes
  );
  // #endregion Long Position Sizes

  // #region Short Position Sizes
  const v1ArbitrumShortPositionSizes = positionStatsInfo?.[ARBITRUM]?.totalShortPositionSizes;
  const v1BscShortPositionSizes = positionStatsInfo?.totalShortPositionSizes
   ? positionStatsInfo.totalShortPositionSizes 
   : 0n;

  const v2ArbitrumShortPositionSizes = v2ArbitrumOverview.totalShortPositionSizes;
  const v2BscShortPositionSizes = v2BscOverview.totalShortPositionSizes;

  const totalShortPositionSizes = sumBigInts(
    // v1ArbitrumShortPositionSizes,
    v1BscShortPositionSizes,
    // v2ArbitrumShortPositionSizes,
    // v2BscShortPositionSizes
  );
  // #endregion Short Position Sizes

  // #region Fees
  const v1ArbitrumWeeklyFees = v1ArbitrumFees?.weeklyFees;
  const v1BscWeeklyFees = v1BscFees?.weeklyFees;

  const v2ArbitrumWeeklyFees = v2ArbitrumOverview?.weeklyFees;
  const v2BscWeeklyFees = v2BscOverview?.weeklyFees;

  const totalWeeklyFeesUsd = sumBigInts(
    // v1ArbitrumWeeklyFees,
    v1BscWeeklyFees,
    // v2ArbitrumWeeklyFees,
    // v2BscWeeklyFees
  );
  // #endregion Fees

  const dailyVolumeEntries = useMemo(
    () => ({
      // "Ronin": v1ArbitrumDailyVolume,
      // "V2 Ronin": v2ArbitrumOverview?.dailyVolume,
      "Bsc": ethers.parseUnits(v1BscDailyVolume.toString(), USD_DECIMALS),
      // "V2 Bsc": v2BscOverview?.dailyVolume,
    }),
    [
      // v1ArbitrumDailyVolume, 
      v1BscDailyVolume, 
      // v2ArbitrumOverview?.dailyVolume, 
      // v2BscOverview?.dailyVolume
    ]
  );

  const openInterestEntries = useMemo(
    () => ({
      "Ronin": v1ArbitrumOpenInterest,
      "V2 Ronin": v2ArbitrumOpenInterest,
      "Bsc": v1BscOpenInterest,
      "V2 Bsc": v2BscOpenInterest,
    }),
    [v1ArbitrumOpenInterest, v1BscOpenInterest, v2ArbitrumOpenInterest, v2BscOpenInterest]
  );

  const totalLongPositionSizesEntries = useMemo(
    () => ({
      // "Ronin": v1ArbitrumLongPositionSizes,
      // "V2 Ronin": v2ArbitrumLongPositionSizes,
      "Bsc": v1BscLongPositionSizes,
      // "V2 Bsc": v2BscLongPositionSizes,
    }),
    [
      // v1ArbitrumLongPositionSizes,
      v1BscLongPositionSizes,
      // v2ArbitrumLongPositionSizes,
      // v2BscLongPositionSizes,
    ]
  );

  const totalShortPositionSizesEntries = useMemo(
    () => ({
      // "Ronin": v1ArbitrumShortPositionSizes,
      // "V2 Ronin": v2ArbitrumShortPositionSizes,
      "Bsc": v1BscShortPositionSizes,
      // "V2 Bsc": v2BscShortPositionSizes,
    }),
    [
      // v1ArbitrumShortPositionSizes,
      v1BscShortPositionSizes,
      // v2ArbitrumShortPositionSizes,
      // v2BscShortPositionSizes,
    ]
  );

  const weeklyFeesEntries = useMemo(
    () => ({
      // "Ronin": v1ArbitrumWeeklyFees,
      // "V2 Ronin": v2ArbitrumWeeklyFees,
      "Bsc": v1BscWeeklyFees,
      // "V2 Bsc": v2BscWeeklyFees,
    }),
    [
      // v1ArbitrumWeeklyFees, 
      v1BscWeeklyFees, 
      // v2ArbitrumWeeklyFees, 
      // v2BscWeeklyFees
    ]
  );

  const [formattedDuration, setFormattedDuration] = useState(() => getFormattedFeesDuration());

  useEffect(() => {
    const interval = setInterval(() => {
      setFormattedDuration(getFormattedFeesDuration());
    }, 1000 * 60);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="App-card">
      <div className="App-card-title">
        <Trans>Overview</Trans>
      </div>
      <div className="App-card-divider"></div>
      <div className="App-card-content">
        <div className="App-card-row">
          <div className="label">
            <Trans>TVL</Trans>
          </div>
          <div>
            <TooltipComponent
              handle={`$${formatAmount(displayTvl, USD_DECIMALS, 0, true)}`}
              position="bottom-end"
              content={
                <>
                  <Trans>Total value locked takes into account:</Trans>
                  <br />
                  <ul className="my-8 list-disc">
                    <li className="p-2">MONEY Staked</li>
                    <li className="p-2">MLP Pool</li>
                    {/* <li className="p-2">GM Pools</li> */}
                  </ul>
                  {/* <StatsTooltipRow label="Ronin" value={formatAmount(displayTvlArbitrum, USD_DECIMALS, 0, true)} /> */}
                  <StatsTooltipRow label="Bsc" value={formatAmount(displayTvlBsc, USD_DECIMALS, 0, true)} />
                  <div className="!my-8 h-1 bg-gray-800" />
                  <StatsTooltipRow label="Total" value={formatAmount(displayTvl, USD_DECIMALS, 0, true)} />
                </>
              }
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>MLP Pool</Trans>
          </div>
          <div>
            <TooltipComponent
              handle={`$${formatAmount(totalMlpTvl, USD_DECIMALS, 0, true)}`}
              position="bottom-end"
              content={
                <>
                  <Trans>Total value of tokens in the MLP pools.</Trans>
                  <br />
                  <br />
                  {/* <StatsTooltipRow label="Ronin" value={formatAmount(mlpTvlArbitrum, USD_DECIMALS, 0, true)} /> */}
                  <StatsTooltipRow label="Bsc" value={formatAmount(mlpTvlBsc, USD_DECIMALS, 0, true)} />
                  <div className="my-8 h-1 bg-gray-800" />
                  <StatsTooltipRow label="Total" value={formatAmount(totalMlpTvl, USD_DECIMALS, 0, true)} />
                  <br />
                  <Trans>
                    This value may be higher on other websites due to the collateral of positions being included in the
                    calculation.
                  </Trans>
                </>
              }
            />
          </div>
        </div>
        {/* <div className="App-card-row">
          <div className="label">
            <Trans>GM Pools</Trans>
          </div>
          <div>
            <TooltipComponent
              handle={`$${formatAmount(totalGmTvl, USD_DECIMALS, 0, true)}`}
              position="bottom-end"
              content={
                <>
                  <Trans>Total value of tokens in GM Pools.</Trans>
                  <br />
                  <br />
                  <StatsTooltipRow label="Ronin" value={formatAmount(gmTvlArbitrum, USD_DECIMALS, 0, true)} />
                  <StatsTooltipRow label="Bsc" value={formatAmount(gmTvlBsc, USD_DECIMALS, 0, true)} />
                  <div className="!my-8 h-1 bg-gray-800" />
                  <StatsTooltipRow label="Total" value={formatAmount(totalGmTvl, USD_DECIMALS, 0, true)} />
                </>
              }
            />
          </div>
        </div> */}
        <div className="App-card-row">
          <div className="label">
            <Trans>24h Volume</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={`$${totalDailyVolume ? totalDailyVolume?.toFixed(2) : 0}`}
              content={<ChainsStatsTooltipRow entries={dailyVolumeEntries} />}
            />
          </div>
        </div>
        {/* <div className="App-card-row">
          <div className="label">
            <Trans>Open Interest</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={`$${formatAmount(totalOpenInterest, USD_DECIMALS, 0, true)}`}
              content={<ChainsStatsTooltipRow entries={openInterestEntries} />}
            />
          </div>
        </div> */}
        <div className="App-card-row">
          <div className="label">
            <Trans>Long Positions</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={formatAmountHuman(totalLongPositionSizes, USD_DECIMALS, true, 2)}
              content={<ChainsStatsTooltipRow entries={totalLongPositionSizesEntries} />}
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>Short Positions</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={formatAmountHuman(totalShortPositionSizes, USD_DECIMALS, true, 2)}
              content={<ChainsStatsTooltipRow entries={totalShortPositionSizesEntries} />}
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>Fees for the past</Trans> {formattedDuration}
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={`$${formatAmount(totalWeeklyFeesUsd, USD_DECIMALS, 2, true)}`}
              content={<ChainsStatsTooltipRow entries={weeklyFeesEntries} />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
